import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import { openSupplierReturnNoteReport } from '../../../utils/openInventoryReport';
import { canPrintSupplierReturnNote } from '../../../utils/documentPrintGate';
import { useAuth } from '../../../contexts/AuthContext';
import ProcurementDocuments from '../../Common/ProcurementDocuments';

const SupplierReturnNoteDetails = ({ open, onClose, returnNote, onReturnNoteUpdate }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [locations, setLocations] = useState([]);
  const [returnTypes, setReturnTypes] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});

  // `location` has no cross-service ref (it lives in admin-service), and
  // `itemReturnReason` is never populated by the backend — resolve both client-side.
  useEffect(() => {
    if (!open) return;
    inventoryApi.getWarehouses().then(res => setLocations(res.data || [])).catch(() => {});
    inventoryApi.getReturnTypes().then(res => setReturnTypes(res.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[SupplierReturnNoteDetails] could not load location info:', e.message);
      }
    })();
  }, [open]);

  if (!returnNote || !open) return null;

  const tabs = [
    {
      id: 'overview',
      name: 'Overview',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'items',
      name: 'Items',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      badge: (returnNote.returnItems || []).length
    },
    {
      id: 'supplier',
      name: 'Supplier',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      id: 'activity',
      name: 'Activity',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  const handleApprove = async () => {
    setLoading(true);
    try {
      await inventoryApi.approveSupplierReturnNote(returnNote._id, { approved: true });
      toast.success('Return note approved successfully');
      if (onReturnNoteUpdate) {
        onReturnNoteUpdate();
      }
      setShowApproveConfirm(false);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error approving return note:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to approve return note');
      setShowApproveConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error(error?.response?.data?.message || 'Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    try {
      await inventoryApi.rejectSupplierReturnNote(returnNote._id, {
        reason: rejectReason,
        comments: rejectReason
      });
      toast.success('Return note rejected successfully');
      if (onReturnNoteUpdate) {
        onReturnNoteUpdate();
      }
      setShowRejectConfirm(false);
      setRejectReason('');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error rejecting return note:', error);
      toast.error(error?.response?.data?.message || 'Failed to reject return note');
      setShowRejectConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (window.confirm('Are you sure you want to submit this return note for approval?')) {
      setLoading(true);
      try {
        await inventoryApi.submitSupplierReturnNoteForApproval(returnNote._id);
        toast.success('Return note submitted for approval successfully');
        if (onReturnNoteUpdate) {
          onReturnNoteUpdate();
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      } catch (error) {
        console.error('Error submitting return note:', error);
        toast.error(error?.response?.data?.message || 'Failed to submit return note');
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      processed: { color: 'bg-blue-100 text-blue-800', label: 'Processed' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      cancelled: { color: 'bg-gray-100 text-gray-600', label: 'Cancelled' },
      completed: { color: 'bg-purple-100 text-purple-800', label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalReturnValue = () => {
    return returnNote.totals?.totalValue || (returnNote.returnItems || []).reduce((sum, item) => sum + (item.totalValue || 0), 0) || 0;
  };

  const grnNumberDisplay = () => {
    return returnNote.grnNumber || returnNote.grn?.grnNumber || (typeof returnNote.grn === 'string' ? returnNote.grn : 'N/A');
  };

  // `location` arrives as a raw ID (no cross-service populate) — resolve against the
  // client-fetched warehouses/locations list; falls back to an already-populated object.
  const resolveLocation = () => {
    if (returnNote.location && typeof returnNote.location === 'object' && returnNote.location.name) {
      return returnNote.location;
    }
    const locationId = returnNote.location?._id || returnNote.location;
    if (!locationId) return null;
    return locations.find(loc => String(loc._id) === String(locationId)) || null;
  };
  const location = resolveLocation();

  // `itemReturnReason` is a ReturnTypes ObjectId that the backend never populates —
  // resolve it against the client-fetched return types list.
  const resolveReasonName = (item) => {
    const raw = item.itemReturnReason || item.reason;
    if (raw && typeof raw === 'object') return raw.name || 'N/A';
    if (!raw) return 'N/A';
    const found = returnTypes.find(type => String(type._id) === String(raw));
    return found?.name || 'N/A';
  };

  const handlePrint = async () => {
    await openSupplierReturnNoteReport(returnNote, companyInfo);
  };

  const DetailItem = ({ label, value }) => (
    <div className="mb-2">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-xs font-medium text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-7xl bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[95vh] overflow-hidden flex">

        {/* Vertical Tabs Sidebar */}
        <div className="w-56 bg-gray-50 border-r border-gray-200 flex-shrink-0">
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                <svg className="w-4 h-4 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xs font-semibold text-gray-900">Supplier Return Note</h2>
                <p className="text-xs text-gray-500">{returnNote.returnNoteNumber || `RN-${returnNote._id?.slice(-6)}`}</p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {getStatusBadge(returnNote.status)}
            </div>
          </div>

          <nav className="p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`${activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                    {tab.icon}
                  </div>
                  <span>{tab.name}</span>
                </div>
                {!!tab.badge && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === tab.id
                      ? 'bg-indigo-200 text-indigo-800'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with Actions */}
          <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-sm font-semibold text-gray-900">
                  {returnNote.returnNoteNumber || `RN-${returnNote._id?.slice(-6)}`}
                </h1>
                <p className="text-xs text-gray-500">
                  Created {formatDate(returnNote.createdAt)}
                  {returnNote.status === 'draft' && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Ready to Submit
                    </span>
                  )}
                  {returnNote.status === 'pending_approval' && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Awaiting Approval
                    </span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {canPrintSupplierReturnNote(returnNote.status) && (
                  <button
                    onClick={handlePrint}
                    title="Print PDF"
                    className="inline-flex items-center px-3 py-1.5 bg-white text-gray-700 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print PDF
                  </button>
                )}

                {returnNote.status === 'draft' && (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {loading ? 'Submitting...' : 'Submit'}
                  </button>
                )}

                {returnNote.status === 'pending_approval' && (
                  <>
                    <button
                      onClick={() => setShowApproveConfirm(true)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-medium rounded hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {loading ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setShowRejectConfirm(true)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-medium rounded hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {loading ? 'Rejecting...' : 'Reject'}
                    </button>
                  </>
                )}

                {(['approved', 'rejected', 'processed', 'completed'].includes(returnNote.status)) && (
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    returnNote.status === 'approved' ? 'bg-green-100 text-green-800'
                      : returnNote.status === 'rejected' ? 'bg-red-100 text-red-800'
                      : returnNote.status === 'processed' ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {returnNote.status === 'approved' ? 'Return Approved'
                      : returnNote.status === 'rejected' ? 'Return Rejected'
                      : returnNote.status === 'processed' ? 'Return Processed'
                      : 'Return Completed'}
                  </span>
                )}

                <button
                  onClick={onClose}
                  disabled={loading}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {(error || success) && (
            <div className="px-4 py-2 border-b border-gray-200">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2">
                  <span className="text-xs text-red-700 flex-1">{error}</span>
                  <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 rounded p-2 flex items-center gap-2">
                  <span className="text-xs text-green-700 flex-1">{success}</span>
                  <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {renderConfirmationModals()}
    </div>
  );

  function renderTabContent() {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'items':
        return renderItemsTab();
      case 'supplier':
        return renderSupplierTab();
      case 'activity':
        return renderActivityTab();
      default:
        return renderOverviewTab();
    }
  }

  function renderOverviewTab() {
    return (
      <div className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-700">Total Items</p>
            <p className="text-base font-bold text-blue-800">{(returnNote.returnItems || []).length}</p>
          </div>
          <div className="px-3 py-2 bg-green-50 border border-green-200 rounded">
            <p className="text-xs text-green-700">Total Value</p>
            <p className="text-base font-bold text-green-800">{formatCurrency(totalReturnValue())}</p>
          </div>
          <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded">
            <p className="text-xs text-purple-700">Supplier</p>
            <p className="text-sm font-bold text-purple-800 truncate">{returnNote.supplierName || returnNote.supplier?.name || 'N/A'}</p>
          </div>
          <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded">
            <p className="text-xs text-orange-700">Return Date</p>
            <p className="text-sm font-bold text-orange-800">{formatDate(returnNote.returnDate)}</p>
          </div>
        </div>

        {/* Return Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Return Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem label="Return Note Number" value={returnNote.returnNoteNumber || `RN-${returnNote._id?.slice(-6)}`} />
              <DetailItem label="Created Date" value={formatDate(returnNote.createdAt)} />
              <DetailItem label="Status" value={returnNote.status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} />
            </div>
            <div className="space-y-2">
              <DetailItem label="Location" value={location?.name} />
              <DetailItem label="Source GRN" value={grnNumberDisplay()} />
              <DetailItem label="Total Items" value={`${(returnNote.returnItems || []).length} items`} />
              {returnNote.debitNoteNumber && (
                <DetailItem label="Debit Note (payment set-off)" value={returnNote.debitNoteNumber} />
              )}
            </div>
          </div>
        </div>

        {/* User Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">User Activity</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Created By</p>
              <p className="text-xs font-medium text-gray-900">{returnNote.createdByUserName || 'Unknown User'}</p>
              <p className="text-xs text-gray-400">{formatDate(returnNote.createdAt)}</p>
            </div>
            {returnNote.updatedByUserName && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Last Updated By</p>
                <p className="text-xs font-medium text-gray-900">{returnNote.updatedByUserName}</p>
                <p className="text-xs text-gray-400">{formatDate(returnNote.updatedAt)}</p>
              </div>
            )}
            {returnNote.approvedByUserName && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Approved By</p>
                <p className="text-xs font-medium text-gray-900">{returnNote.approvedByUserName}</p>
                <p className="text-xs text-gray-400">{formatDate(returnNote.approvedAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {returnNote.notes && (
          <div className="bg-blue-50 rounded border border-blue-200 p-3">
            <h3 className="text-xs font-semibold text-blue-900 mb-1">Notes</h3>
            <p className="text-xs text-blue-800">{returnNote.notes}</p>
          </div>
        )}

        {/* Supporting documents */}
        <ProcurementDocuments docType="supplier-return-notes" docId={returnNote._id}
          canEdit={hasPermission('INVENTORY:SUPPLIER_RETURN_NOTES:UPDATE') || hasPermission('INVENTORY:SUPPLIER_RETURN_NOTES:CREATE')} />
      </div>
    );
  }

  function renderItemsTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Return Items</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {(returnNote.returnItems || []).length} Items
            </span>
          </div>

          {returnNote.returnItems && returnNote.returnItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Return Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Unit Price</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Total</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Reason</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {returnNote.returnItems.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-gray-900">{item.itemName || item.item?.name || 'Unknown Item'}</div>
                        <div className="text-xs text-gray-500 font-mono">{item.itemCode || item.item?.code || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900 text-right font-medium">
                        {item.returnQuantity || 0} {item.unit || 'PCS'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-xs font-medium text-gray-900 text-right">{formatCurrency(item.totalValue)}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        <span className="line-clamp-2">{resolveReasonName(item)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-xs text-gray-400">No return items</div>
          )}
        </div>

        {/* Return Summary */}
        <div className="bg-indigo-50 rounded border border-indigo-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Return Summary</h3>
          <div className="max-w-xs ml-auto space-y-1.5">
            <div className="border-t border-indigo-200 pt-1.5 flex justify-between">
              <span className="text-sm font-semibold text-gray-900">Total Return Value:</span>
              <span className="text-sm font-semibold text-indigo-700">{formatCurrency(totalReturnValue())}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderSupplierTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Supplier Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem label="Supplier Name" value={returnNote.supplierName || returnNote.supplier?.name || 'N/A'} />
              <DetailItem label="Supplier Code" value={returnNote.supplierCode || returnNote.supplier?.code || 'N/A'} />
              <DetailItem label="Contact Person" value={returnNote.supplier?.contactInfo?.contactPerson || 'N/A'} />
            </div>
            <div className="space-y-2">
              <DetailItem label="Email" value={returnNote.supplier?.contactInfo?.email || 'N/A'} />
              <DetailItem label="Phone" value={returnNote.supplier?.contactInfo?.phone || 'N/A'} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Location Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem label="Location Name" value={location?.name || 'N/A'} />
              <DetailItem label="Location Code" value={location?.code || 'N/A'} />
            </div>
            <div className="space-y-2">
              <DetailItem label="Type" value={location?.type ? location.type.charAt(0).toUpperCase() + location.type.slice(1) : 'N/A'} />
              <DetailItem
                label="Address"
                value={[location?.address?.street, location?.address?.city, location?.address?.state]
                  .filter(Boolean).join(', ') || 'N/A'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderActivityTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-4 h-4 text-indigo-600 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            People Involved
          </h3>
          <div className="space-y-3">
            {returnNote.createdByUserName && (
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-blue-600">{returnNote.createdByUserName?.charAt(0) || 'U'}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-900">Created By {returnNote.createdByUserName}</p>
                  <p className="text-xs text-gray-500">{formatDate(returnNote.createdAt)}</p>
                </div>
              </div>
            )}
            {returnNote.updatedByUserName && (
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-yellow-600">{returnNote.updatedByUserName?.charAt(0) || 'U'}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-900">Updated By {returnNote.updatedByUserName}</p>
                  <p className="text-xs text-gray-500">{formatDate(returnNote.updatedAt)}</p>
                </div>
              </div>
            )}
            {returnNote.approvedByUserName && (
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-green-600">{returnNote.approvedByUserName?.charAt(0)}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-900">Approved By {returnNote.approvedByUserName}</p>
                  <p className="text-xs text-gray-500">{formatDate(returnNote.approvedAt)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-4 h-4 text-purple-600 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Activity Timeline
          </h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900">Created</p>
                <p className="text-xs text-gray-600">{formatDate(returnNote.createdAt)}</p>
                {returnNote.createdByUserName && <p className="text-xs text-gray-400">By {returnNote.createdByUserName}</p>}
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900">Last Updated</p>
                <p className="text-xs text-gray-600">{formatDate(returnNote.updatedAt)}</p>
                {returnNote.updatedByUserName && <p className="text-xs text-gray-400">By {returnNote.updatedByUserName}</p>}
              </div>
            </div>
            {returnNote.approvedAt && (
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900">Approved</p>
                  <p className="text-xs text-gray-600">{formatDate(returnNote.approvedAt)}</p>
                  {returnNote.approvedByUserName && <p className="text-xs text-gray-400">By {returnNote.approvedByUserName}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderConfirmationModals() {
    return (
      <>
        {showApproveConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Approve Return Note</h3>
                    <p className="text-xs text-gray-500">This action cannot be undone</p>
                  </div>
                </div>

                <div className="bg-green-50 rounded p-3 mb-4 border border-green-200">
                  <p className="text-xs font-medium text-green-800">
                    Approve return note {returnNote.returnNoteNumber || `RN-${returnNote._id?.slice(-6)}`}?
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    This takes the returned items out of stock and posts the GL entry. Then raise its debit note from Debit Notes → From Supplier Returns to set it off in supplier payments. Total: {formatCurrency(totalReturnValue())}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowApproveConfirm(false)}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Approving...' : 'Approve Return'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRejectConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Reject Return Note</h3>
                    <p className="text-xs text-gray-500">This action cannot be undone</p>
                  </div>
                </div>

                <div className="bg-red-50 rounded p-3 mb-3 border border-red-200">
                  <p className="text-xs font-medium text-red-800">
                    Reject return note {returnNote.returnNoteNumber || `RN-${returnNote._id?.slice(-6)}`}?
                  </p>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    placeholder="Please provide a reason for rejection..."
                    disabled={loading}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowRejectConfirm(false); setRejectReason(''); }}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={loading || !rejectReason.trim()}
                    className="flex-1 px-3 py-1.5 text-xs bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Rejecting...' : 'Reject Return'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
};

export default SupplierReturnNoteDetails;
